import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import { StatCard } from '@/components/ui/stat-card';
import { SparkChart } from '@/components/ui/spark-chart';
import { ModeSplitBar } from '@/components/ui/mode-split-bar';
import { DataTable, type Column } from '@/components/data-table/data-table';
import { ErrorState } from '@/components/state/error-state';
import { Badge } from '@/components/ui/badge';
import { queryKeys } from '@/lib/query-keys';
import { formatNumber, formatRelative } from '@/lib/format';
import {
  getDashboardStats,
  type RecentSignup,
  type UrgentTicket,
  type TimeSeriesPoint,
} from '@/features/dashboard/api';

const PERIODS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
];

interface DashboardSearch {
  period?: number;
}

export const Route = createFileRoute('/_authenticated/')({
  validateSearch: (search: Record<string, unknown>): DashboardSearch => ({
    period: typeof search['period'] === 'number' ? search['period'] : undefined,
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/' });
  const period = search.period ?? 30;

  const query = useQuery({
    queryKey: queryKeys.dashboard.stats(period),
    queryFn: () => getDashboardStats(period),
    placeholderData: (prev) => prev,
  });

  const data = query.data;

  const signupColumns: Column<RecentSignup>[] = [
    {
      key: 'user',
      header: 'User',
      render: (u) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={u.display_name} url={u.display_image_url} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{u.display_name || 'Unnamed'}</div>
            {u.username ? <div className="wb-tiny">@{u.username}</div> : null}
          </div>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (u) => (
        <Badge tone={u.subscription_tier ? 'honey' : 'neutral'}>
          {u.subscription_tier || 'free'}
        </Badge>
      ),
    },
    {
      key: 'joined',
      header: 'Joined',
      align: 'right',
      render: (u) => <span className="wb-small">{formatRelative(u.created_at)}</span>,
    },
  ];

  const ticketColumns: Column<UrgentTicket>[] = [
    {
      key: 'ticket',
      header: 'Ticket',
      render: (t) => (
        <div>
          <div style={{ fontWeight: 600 }}>#{t.ticket_number}</div>
          <div className="wb-tiny">{t.category}</div>
        </div>
      ),
    },
    {
      key: 'message',
      header: 'Message',
      render: (t) => (
        <span
          className="wb-small"
          style={{
            display: 'block',
            maxWidth: 360,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {t.message}
        </span>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (t) => <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>,
    },
    {
      key: 'created',
      header: 'Opened',
      align: 'right',
      render: (t) => <span className="wb-small">{formatRelative(t.created_at)}</span>,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div className="flex flex-col gap-1.5">
          <span className="wb-eyebrow">Overview</span>
          <h1 className="wb-h1">Dashboard</h1>
          <p className="wb-sub">Platform health and activity at a glance.</p>
        </div>
        <Select
          size="sm"
          options={PERIODS}
          value={String(period)}
          onChange={(e) => navigate({ to: '/', search: { period: Number(e.target.value) } })}
          style={{ width: 150 }}
        />
      </div>

      {query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          {/* KPI row */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" style={{ marginBottom: 20 }}>
            <StatCard label="Total users" value={kpi(data?.kpis.total_users, query.isLoading)} />
            <StatCard label="New today" value={kpi(data?.kpis.new_users_today, query.isLoading)} />
            <StatCard
              label="New this week"
              value={kpi(data?.kpis.new_users_this_week, query.isLoading)}
            />
            <StatCard label="Trips today" value={kpi(data?.kpis.trips_today, query.isLoading)} />
            <StatCard
              label="Trips yesterday"
              value={kpi(data?.kpis.trips_yesterday, query.isLoading)}
            />
            <StatCard label="Open tickets" value={kpi(data?.kpis.open_tickets, query.isLoading)} />
          </div>

          {/* Trend charts */}
          <div className="grid gap-3 sm:grid-cols-2" style={{ marginBottom: 20 }}>
            <ChartCard title="User growth" series={data?.user_growth} color="var(--chart-1)" />
            <ChartCard title="Trips" series={data?.trip_trends} color="var(--chart-2)" />
            <ChartCard
              title="Tickets opened"
              series={data?.support_opened}
              color="var(--chart-5)"
            />
            <ChartCard
              title="Tickets resolved"
              series={data?.support_resolved}
              color="var(--chart-3)"
            />
          </div>

          {/* Modal split */}
          <Card style={{ marginBottom: 20 }}>
            <CardContent>
              <div className="wb-eyebrow" style={{ marginBottom: 12 }}>
                Distance by mode
              </div>
              {data ? (
                <ModeSplitBar split={data.modal_split} />
              ) : (
                <span
                  className="wb-skeleton"
                  style={{ width: '100%', height: 44, display: 'block' }}
                />
              )}
            </CardContent>
          </Card>

          {/* Activity tables */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
                Recent signups
              </div>
              <DataTable
                columns={signupColumns}
                rows={data?.recent_signups}
                rowKey={(u) => u.id}
                isLoading={query.isLoading}
                onRowClick={(u) => navigate({ to: '/users/$userId', params: { userId: u.id } })}
                empty={{ title: 'No recent signups' }}
              />
            </div>
            <div>
              <div className="wb-eyebrow" style={{ marginBottom: 10 }}>
                Urgent tickets
              </div>
              <DataTable
                columns={ticketColumns}
                rows={data?.urgent_tickets}
                rowKey={(t) => t.id}
                isLoading={query.isLoading}
                onRowClick={(t) => navigate({ to: '/support/$id', params: { id: t.id } })}
                empty={{ title: 'No urgent tickets', hint: 'Nothing needs attention right now.' }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function kpi(value: number | undefined, loading: boolean | undefined): ReactNode {
  if (loading && value == null)
    return (
      <span className="wb-skeleton" style={{ width: 64, height: 22, display: 'inline-block' }} />
    );
  return formatNumber(value);
}

function priorityTone(priority: string): 'danger' | 'warning' | 'neutral' {
  const p = priority?.toLowerCase();
  if (p === 'urgent' || p === 'high' || p === 'critical') return 'danger';
  if (p === 'medium' || p === 'normal') return 'warning';
  return 'neutral';
}

function ChartCard({
  title,
  series,
  color,
}: {
  title: string;
  series: TimeSeriesPoint[] | null | undefined;
  color: string;
}) {
  const total = (series ?? []).reduce((sum, p) => sum + (p.count ?? 0), 0);
  return (
    <Card>
      <CardContent>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <span className="wb-eyebrow">{title}</span>
          <span className="wb-mono wb-small">{total.toLocaleString()}</span>
        </div>
        <SparkChart data={series} color={color} />
      </CardContent>
    </Card>
  );
}

function Avatar({ name, url }: { name?: string | null; url?: string | null }) {
  const initial = (name || '?').charAt(0).toUpperCase();
  return url ? (
    <img
      src={url}
      alt=""
      style={{ width: 34, height: 34, borderRadius: 999, objectFit: 'cover', flexShrink: 0 }}
    />
  ) : (
    <div
      aria-hidden
      style={{
        width: 34,
        height: 34,
        borderRadius: 999,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--honey-wash)',
        color: 'var(--honey-700)',
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      {initial}
    </div>
  );
}
